import { Type, type Static } from '@sinclair/typebox';
import { AttachmentSchema } from '../../shared/attachment/attachment.schema';
import { reactionCounterSchema } from '../../shared/reaction/reaction-counter.schema';
import { MinimalUser, minimalUserSchema } from '../../shared/user/user.schema';

export const BasePostSchema = Type.Object({
  object: Type.Literal('Post'),
  id: Type.String({ description: 'The ID of the post for the provider.' }),
  share_url: Type.String({ description: 'The URL to share the post.' }),
  created_at: Type.String({
    description: 'The creation date of the post. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).',
  }),
  title: Type.Optional(Type.String({ description: 'The title of the post.' })),
  text: Type.String({ description: 'The text content of the post.' }),
  author: Type.Object(minimalUserSchema, { description: 'The author of the post.' }),
  user_reacted: Type.Union([Type.Boolean(), Type.String()], {
    description:
      'If the account owner has reacted to this post, the value of the reaction (emoji unicode or reaction type), `true` if no details on the reaction, else `false`.',
  }),
  permissions: Type.Object(
    {
      can_share: Type.Boolean({ description: 'Whether the account owner can share the post.' }),
      can_react: Type.Boolean({ description: 'Whether the account owner can react to the post.' }),
      can_post_comments: Type.Boolean({ description: 'Whether the account owner can comment on the post.' }),
    },
    { description: 'The permissions for the account owner on the post.' },
  ),
  reactions_counter: Type.Union([reactionCounterSchema, Type.Null()], {
    description: 'The counter of reactions to the post. `null` if counter is hidden.',
  }),
  comments_counter: Type.Union([Type.Number(), Type.Null()], {
    description: 'The number of comments to the post. `null` if counter is hidden.',
  }),
  reposts_counter: Type.Union([Type.Number(), Type.Null()], {
    description: 'The number of reposts of the post. `null` if counter is hidden.',
  }),
  poll: Type.Optional(
    Type.Object(
      {
        id: Type.String({ description: 'The ID of the poll.' }),
        total_votes_count: Type.Number({ description: 'The total number of votes for the poll.' }),
        question: Type.String({ description: 'The question of the poll.' }),
        is_open: Type.Boolean({
          description: 'Whether the poll is open. If the poll is closed, the user can only see the results.',
        }),
        options: Type.Array(
          Type.Object({
            id: Type.String({ description: 'The ID of the option.' }),
            text: Type.String({ description: 'The displayed text of the option.' }),
            win: Type.Boolean({ description: 'Whether the option is the winning option.' }),
            votes_count: Type.Number({ description: 'The number of votes for this option.' }),
          }),
        ),
      },
      { description: 'Data about the poll if the post is a poll.' },
    ),
  ),
  event: Type.Optional(
    Type.Object(
      {
        id: Type.String({ description: 'The ID of the event.' }),
        name: Type.String({ description: 'The name of the event.' }),
        url: Type.Optional(Type.String({ description: 'The link to the event.' })),
        online: Type.Boolean({ description: 'Whether the event is an online event.' }),
        location: Type.Optional(Type.String({ description: 'The location of the event.' })),
        description: Type.Optional(Type.String({ description: 'The description of the event.' })),
        public_picture_url: Type.Optional(Type.String({ format: 'uri', description: 'Public url to the picture of the event.' })),
        starts_on: Type.Optional(
          Type.String({
            description: 'The date when the event will start. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).',
          }),
        ),
        ends_on: Type.Optional(
          Type.String({
            description: 'The date when the event will end. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).',
          }),
        ),
      },
      { description: 'Data about the event if the post is an event.' },
    ),
  ),
  attachments: Type.Array(AttachmentSchema, { description: 'List of post attachments.' }),
  analytics: Type.Optional(
    Type.Object(
      {
        impressions_counter: Type.Optional(Type.Number({ description: 'The number of impressions of the post.' })),
        engagements_counter: Type.Optional(Type.Number({ description: 'The number of engagements of the post.' })),
        engagement_rate: Type.Optional(Type.Number({ description: 'The engagement rate of the post.' })),
        clicks_counter: Type.Optional(Type.Number({ description: 'The number of clicks of the post.' })),
        clickthrough_rate: Type.Optional(Type.Number({ description: 'The clickthrough rate of the post.' })),
        page_viewers_from_this_post_counter: Type.Optional(
          Type.Number({ description: 'The number of page viewers from this post.' }),
        ),
        followers_gained_from_this_post_counter: Type.Optional(
          Type.Number({ description: 'The number of followers gained from this post.' }),
        ),
        users_reached_counter: Type.Optional(Type.Number({ description: 'The number of users reached from this post.' })),
      },
      { description: 'Analytics data of the post.' },
    ),
  ),
  specifics: Type.Optional(Type.Any({ description: 'Object containing provider-specific post data.' })),
});

export const PostPreviewSchema = Type.Object(
  {
    object: Type.Literal('PostPreview'),
    id: Type.String({ description: 'The ID of the post for the provider.' }),
    share_url: Type.Optional(Type.String({ description: 'The URL to share the post.' })),
    text: Type.Optional(Type.String({ description: 'The text content of the post.' })),
    author: Type.Optional(Type.Object(minimalUserSchema, { description: 'The author of the post.' })),
    attachments: Type.Optional(
      Type.Array(
        Type.Object({
          object: Type.Literal('Attachment'),
          type: Type.Literal('img'),
          url: Type.String({ description: 'The URL of the attachment.' }),
        }),
        { description: 'Attachments of the parent post (e.g. post image).' },
      ),
    ),
  },
  { description: 'The amount of available data may vary depending on the provider.' },
);

export type PostPreview<UserSpecifics = unknown> = Omit<Static<typeof PostPreviewSchema>, 'author'> & {
  author?: MinimalUser<UserSpecifics>;
};

export const PostSchema = Type.Composite([
  BasePostSchema,
  Type.Object({
    is_repost: Type.Boolean({
      description: '`true` if this post is reposted by someone without quote. Quoted post does not qualify as a repost.',
    }),
    reposted_by: Type.Optional(
      Type.Object(minimalUserSchema, { description: 'The user who reposted the post if `is_repost` is `true`.' }),
    ),
    quoted_post: Type.Optional(Type.Composite([BasePostSchema], { description: 'The post that is quoted in this post.' })),
  }),
]);

type BasePost<UserSpecifics = unknown, Specifics = unknown> = Omit<Static<typeof BasePostSchema>, 'author' | 'specifics'> & {
  author: MinimalUser<UserSpecifics>;
  specifics?: Specifics;
};

export type Post<UserSpecifics = unknown, Specifics = unknown> = Omit<
  Static<typeof PostSchema>,
  'reposted_by' | 'author' | 'quoted_post' | 'specifics'
> & {
  author: MinimalUser<UserSpecifics>;
  reposted_by?: MinimalUser<UserSpecifics>;
  quoted_post?: BasePost<UserSpecifics, Specifics>;
  specifics?: Specifics;
};
